import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Avatar, Box, CardInfo, Icon, Link, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import type ContractVersionRequest from 'modules/contracts/store/models/ContractVersionRequest'

type Props = {
  contractVersionRequest: ContractVersionRequest
  employeeName: string
}

const getTitle = ({
  actionType,
  requesterName,
  employeeName,
}: {
  actionType: string
  requesterName: string
  employeeName: string
}) => {
  switch (actionType) {
    case FLOWS.promotion:
      return i18n.t('contract_versions.approval_banner.promotion.title', {
        requesterName: requesterName,
        employeeName: employeeName,
      })
    case FLOWS.evolution:
      return i18n.t('contract_versions.approval_banner.evolution.title', {
        requesterName: requesterName,
        employeeName: employeeName,
      })
  }
}

const ApprovalBanner = ({ contractVersionRequest, employeeName }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const params = useParams()

  const requesterId = contractVersionRequest.attributes.approval_author_id || ''
  const requestedAt = contractVersionRequest.attributes.approval_request_created_at || ''
  const requester = users.find({ id: requesterId })

  if (!requester) return null

  const actionType = contractVersionRequest.attributes.approval_action_type || ''

  const navigateToApproval = () => {
    track(trackingEvents.employee.contracts.goToContractRequest, {
      source: 'contract_page',
      approval_status: contractVersionRequest.attributes.status,
      request_type: actionType,
      request_date: requestedAt,
      request_reason: contractVersionRequest.attributes.request_details,
    })
    navigate(
      navigation.employees.id.contractVersions.approval_request.fullPath({
        id: Number(params.id),
        contractVersionId: contractVersionRequest.id,
      })
    )
  }

  const title = getTitle({
    actionType: actionType,
    requesterName: requester.fullName(),
    employeeName: employeeName,
  })

  return (
    <Box>
      <CardInfo>
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Box alignItems="flexStart" flexDirection="row" gap="s12">
            <Avatar
              size="s24"
              url={buildUrl(requester.avatar)}
              label={requester.fullName() || ''}
            />
            <Box flexDirection="column" gap="s12">
              <Text size={'200'} weight="semibold">
                {title}
              </Text>
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Tag type="champagne">{i18n.t('contract_versions.approval_banner.tag')}</Tag>
                <Box flexDirection="row" alignItems="center">
                  <Icon.Small icon="clock" />
                  <Text size="200">
                    {i18n.t('contract_versions.approval_banner.date_label', {
                      date: dateBackendToUI(requestedAt),
                    })}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
          <Link.Touchable onClick={navigateToApproval}></Link.Touchable>
        </Box>
      </CardInfo>
    </Box>
  )
}

export default observer(ApprovalBanner)
